import { useCallback, useEffect, useState } from 'react';

import { faCircleNotch, faFileZipper } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaginationState } from '@tanstack/react-table';
import { Download } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DocumentRow from './components/DocumentRow';
import DocumentEditorToolbar from './DocumentEditorToolbar';
import { DocumentFiltersPopup } from './DocumentEditorToolbar/DocumentFilters';
import { DocSortPopup } from './DocumentEditorToolbar/DocumentSort';
import { DownloadFormat } from '../../../../../../../backend/src/types/types';
import { useGetCaseDocThemes } from '../../../../../api/queries/useGetCaseDocThemes';
import DocViewer from '../../components/DocViewer/FullDocViewer';
import { SubToolbar } from '../components/TableToolbar/SubToolbar';
import { TableWrapper } from '../components/TableWrapper';
import { PageName, TrackingEvents } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useCreateDocumentZip } from '@/api/mutations/useCreateDocumentZip';
import { useDownloadDocumentsList } from '@/api/mutations/useDownloadDocumentsList';
import { useDownloadDocumentZip } from '@/api/mutations/useDownloadDocumentZip';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetDocs } from '@/api/queries/useGetDocs';
import { DownloadTableModal } from '@/components/molecules/DownloadTable';
import { DownloadZipModal } from '@/components/molecules/DownloadZipModal';
import EmptyState from '@/components/molecules/EmptyState';
import { FilterPills } from '@/components/molecules/FilterPills';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import Modal from '@/components/molecules/Modals/SettingsModal';
import { useDocumentNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { DocFilters } from '@/types';
import { buildDocumentFilterPills } from '@/utils/buildFilterPills';
import { removeFilterValue } from '@/utils/filterUtils';

const DocumentEditor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const caseId = searchParams.get('caseId');
  const pageNumber = searchParams.get('page') || '0';
  const docId = searchParams.get('docId');

  const [globalFilter, setGlobalFilter] = useState('');
  const [appliedGlobalFilter, setAppliedGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: parseInt(pageNumber), pageSize: 50 });
  const [isProcessingNL, setIsProcessingNL] = useState(false);
  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  const [isDocViewerFullyOpen, setIsDocViewerFullyOpen] = useState(false);

  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);
  const [downloadZipModalIsOpen, setDownloadZipModalIsOpen] = useState(false);
  const [filtersModalIsOpen, setFiltersModalIsOpen] = useState(false);
  const [sortModalIsOpen, setSortModalIsOpen] = useState(false);

  const [filters, setFilters] = useState<DocFilters>({});
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Zip download state
  const [zipExists, setZipExists] = useState(false);
  const [zipInProgress, setZipInProgress] = useState(false);
  const [zipError, setZipError] = useState<string | undefined>();

  const handlePageSizeChange = (newPageSize: number) => {
    const newPaginationState = { pageIndex: 0, pageSize: newPageSize };
    setPagination(newPaginationState);
  };

  useEffect(() => {
    if (docId) {
      setIsDocViewerOpen(true);
      // Add a slight delay before setting isDocViewerFullyOpen to true
      setTimeout(() => setIsDocViewerFullyOpen(true), 300);
    } else {
      setIsDocViewerFullyOpen(false);
      // Add a slight delay before closing the doc viewer
      setTimeout(() => setIsDocViewerOpen(false), 300);
    }
  }, [docId]);

  const { data: docTypes } = useGetCaseDocTypes({ caseId: caseId ?? '' });
  const { data: docThemes } = useGetCaseDocThemes({ caseId: caseId ?? '' });

  const {
    data: responseDocs,
    isLoading: isLoadingDocsTable,
    refetch: refetchDocs,
  } = useGetDocs({
    caseId,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    searchQuery: appliedGlobalFilter,
    processed: filters.processed,
    extensions: filters.extensions,
    startDate: filters.startDate,
    endDate: filters.endDate,
    documentTypes: filters.documentTypes,
    documentThemes: filters.documentThemes,
    hasKeyFacts: filters.hasKeyFacts,
    hasRelevantFacts: filters.hasRelevantFacts,
    sortBy,
    sortOrder,
  });
  const { data: caseData } = useGetCase(caseId ?? '');

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useDocumentNaturalLanguageFilters({
    caseId: caseId ?? '',
    onFiltersChange: (newFilters: DocFilters) => {
      setFilters(newFilters);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    onSearchChange: (searchQuery: string) => {
      setAppliedGlobalFilter(searchQuery);
      setGlobalFilter(searchQuery);
    },
  });

  const onSearchCall = (searchValue?: string, useNaturalLanguage?: boolean) => {
    const value = searchValue ?? globalFilter;

    if (useNaturalLanguage && value.trim()) {
      setIsProcessingNL(true);

      buildFiltersWithNL(value);

      return;
    }

    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    setAppliedGlobalFilter(value);

    // Automatically sort by chunk match count when searching
    if (globalFilter.trim() !== '') {
      setSortBy('chunkMatchCount');
      setSortOrder('DESC');
    }
  };

  useEffect(() => {
    if (!isProcessingNLFiltersQuery) {
      setIsProcessingNL(false);
    }
  }, [isProcessingNLFiltersQuery]);

  const clearSearch = async () => {
    setGlobalFilter('');
    setAppliedGlobalFilter('');
    setIsProcessingNL(false);

    // Clear chunk match count sort when clearing search
    if (sortBy === 'chunkMatchCount') {
      setSortBy('');
      setSortOrder('DESC');
    }
  };

  const handleRemoveFilter = (filterKey: string, value?: unknown) => {
    setFilters((prev) => removeFilterValue(prev, filterKey as keyof DocFilters, value));
  };

  useEffect(() => {
    refetchDocs();
  }, [pagination, appliedGlobalFilter, filters, refetchDocs]);

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  const { fetchDownload, isLoadingDownload } = useDownloadDocumentsList({
    caseId,
  });

  const { createZip } = useCreateDocumentZip({
    caseId: caseId || '',
  });

  const { downloadZip } = useDownloadDocumentZip({
    caseId: caseId || '',
    caseName: caseData?.name || 'Documents',
  });

  const handleDownload = useCallback(
    ({ title, columns, downloadFormat }: { title: string; columns: string[]; downloadFormat: DownloadFormat }) => {
      trackCustomEvent(TrackingEvents.DownloadDocumentList, { downloadFormat, caseId });
      fetchDownload(
        {
          title,
          columns,
          format: downloadFormat,
          searchQuery: appliedGlobalFilter,
          processed: filters.processed ?? undefined,
          extensions: filters.extensions,
          startDate: filters.startDate ?? undefined,
          endDate: filters.endDate ?? undefined,
          documentTypes: filters.documentTypes,
          documentThemes: filters.documentThemes,
          hasKeyFacts: filters.hasKeyFacts ?? undefined,
          hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
        },
        {
          onSuccess: async () => {
            await new Promise((resolve) => setTimeout(resolve, 200));
            setDownloadModalIsOpen(false);
          },
        },
      );
    },
    [caseId, appliedGlobalFilter, filters, fetchDownload],
  );

  // Build filter object for zip creation
  const buildZipFilters = useCallback(() => {
    const hasFilters =
      appliedGlobalFilter ||
      filters.processed !== undefined ||
      (filters.extensions?.length ?? 0) > 0 ||
      filters.startDate ||
      filters.endDate ||
      (filters.documentTypes?.length ?? 0) > 0 ||
      (filters.documentThemes?.length ?? 0) > 0 ||
      filters.hasKeyFacts !== undefined ||
      filters.hasRelevantFacts !== undefined;

    if (!hasFilters) {
      return undefined;
    }

    return {
      searchQuery: appliedGlobalFilter || undefined,
      processed: filters.processed ?? undefined,
      extensions: filters.extensions,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      documentTypes: filters.documentTypes,
      documentThemes: filters.documentThemes,
      hasKeyFacts: filters.hasKeyFacts ?? undefined,
      hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
    };
  }, [appliedGlobalFilter, filters]);

  const handleCheckExisting = useCallback(() => {
    setZipError(undefined);

    const zipFilters = buildZipFilters();

    createZip(zipFilters, {
      onSuccess: (response) => {
        if (response.zipExists) {
          setZipExists(true);
          setZipInProgress(false);
        } else if (response.inProgress) {
          setZipExists(false);
          setZipInProgress(true);
        } else {
          setZipExists(false);
          setZipInProgress(false);
        }
      },
      onError: (error) => {
        setZipError(error instanceof Error ? error.message : 'Failed to check zip status');
      },
    });
  }, [createZip, buildZipFilters]);

  const handleDownloadZip = useCallback(() => {
    const zipFilters = buildZipFilters();

    downloadZip(zipFilters, {
      onSuccess: () => {
        setDownloadZipModalIsOpen(false);
        setZipExists(false);
      },
      onError: (error) => {
        setZipError(error instanceof Error ? error.message : 'Failed to download zip');
      },
    });
  }, [downloadZip, buildZipFilters]);

  // Handler for all documents zip - directly download without modal
  const handleOpenDownloadAllZipModal = useCallback(() => {
    createZip(undefined, {
      onSuccess: (response) => {
        // Download immediately if zip exists
        if (response.zipExists) {
          downloadZip(undefined, {
            onSuccess: () => {},
            onError: (error) => {
              console.error('Failed to download all documents zip:', error);
            },
          });
        } else {
          // If zip doesn't exist or is in progress, show modal to track creation
          setZipExists(response.zipExists || false);
          setZipInProgress(response.inProgress || false);
          setDownloadZipModalIsOpen(true);
        }
      },
      onError: (error) => {
        console.error('Failed to check all documents zip status:', error);
      },
    });
  }, [createZip, downloadZip]);

  useEffect(() => {
    trackPageView(PageName.Documents);
  }, []);

  const handleClear = () => {
    setFilters({});
  };

  return (
    <div className="flex h-full w-full">
      {!isDocViewerFullyOpen && (
        <div
          className="flex h-full flex-col transition-all duration-300 ease-in-out"
          style={{ width: isDocViewerOpen ? '0%' : '100%', opacity: isDocViewerOpen ? 0 : 1 }}
        >
          <DocumentEditorToolbar
            openDownloadModal={() => setDownloadModalIsOpen(true)}
            openDownloadZipModal={() => setDownloadZipModalIsOpen(true)}
            openDownloadAllZipModal={handleOpenDownloadAllZipModal}
            globalFilter={globalFilter}
            openSettingsModal={() => navigate(`/app/chronos/case-editor/settings/documents?caseId=${caseId}`)}
            setGlobalFilter={setGlobalFilter}
            onSearchCall={onSearchCall}
            clearSearch={clearSearch}
            processingNL={isProcessingNL}
            totalPages={responseDocs?.pageCount}
            goToPage={goToPage}
            currentPage={pagination.pageIndex}
            prevPage={prevPage}
            nextPage={nextPage}
            setFiltersModalOpen={setFiltersModalIsOpen}
            filterCount={
              Object.values(filters).filter(
                (value) =>
                  value !== undefined &&
                  value !== null &&
                  value !== '' &&
                  !(Array.isArray(value) && value.length === 0),
              ).length
            }
            setSortModalOpen={setSortModalIsOpen}
            pageSize={pagination.pageSize}
            onPageSizeChange={handlePageSizeChange}
            hasActiveFilters={!!buildZipFilters()}
          />
          <FilterPills pills={buildDocumentFilterPills(filters, handleRemoveFilter)} onClearAll={handleClear} />
          <SubToolbar
            entityType="documents"
            totalCount={responseDocs?.totalCount}
            totalUnfilteredCount={responseDocs?.totalUnfilteredCount}
            totalProcessedCount={responseDocs?.totalProcessedCount}
          />

          <TableWrapper>
            <div className="h-full overflow-y-scroll">
              {isLoadingDocsTable ? (
                <div className="flex h-full min-h-[500px] w-full items-center justify-center">
                  <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                </div>
              ) : (
                <>
                  <div className="flex w-full flex-col gap-4 py-1">
                    {responseDocs && responseDocs.docs && responseDocs.docs.length > 0 ? (
                      <>
                        {responseDocs.docs.map((doc) => {
                          return (
                            <DocumentRow
                              key={doc.id}
                              doc={doc}
                              caseId={caseId || ''}
                              searchQuery={appliedGlobalFilter}
                            />
                          );
                        })}
                      </>
                    ) : (
                      <EmptyState
                        title="No documents found"
                        subtitle="Sorry, we couldn't find any documents matching these criteria"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </TableWrapper>
        </div>
      )}

      <div
        className={`transition-all duration-300 ease-in-out ${
          isDocViewerOpen ? 'w-full translate-x-0 opacity-100' : 'w-0 translate-x-full opacity-0'
        } overflow-hidden bg-white`}
      >
        {docId && <DocViewer docId={docId} caseId={caseId ?? ''} />}
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2 font-semibold text-blue-500">
            <Download />
            Download Documents List
          </div>
        }
        isOpen={downloadModalIsOpen}
        handleClose={() => setDownloadModalIsOpen(false)}
        content={
          <DownloadTableModal
            columnOptions={documentListDownloadOptions}
            downloadTitle={`${caseData?.name} Documents`}
            loadingDownload={isLoadingDownload}
            totalCount={responseDocs?.totalCount}
            handleDownload={handleDownload}
          />
        }
        size="small"
      />
      <CustomModal
        content={
          <DocumentFiltersPopup
            activeFilters={filters}
            setFilters={setFilters}
            handleClose={() => setFiltersModalIsOpen(false)}
            allDocumentTypes={docTypes ?? []}
            docThemes={docThemes ?? []}
            goToPage={goToPage}
          />
        }
        isOpen={filtersModalIsOpen}
        handleClose={() => setFiltersModalIsOpen(false)}
        maxWidth="42rem"
      />
      <CustomModal
        content={
          <DocSortPopup
            activeSortBy={sortBy}
            activeSortOrder={sortOrder}
            setSortBy={setSortBy}
            setSortOrder={setSortOrder}
            handleClose={() => setSortModalIsOpen(false)}
            hasActiveSearch={!!appliedGlobalFilter && appliedGlobalFilter.trim() !== ''}
          />
        }
        isOpen={sortModalIsOpen}
        handleClose={() => setSortModalIsOpen(false)}
        maxWidth="28rem"
      />
      <DownloadZipModal
        isOpen={downloadZipModalIsOpen}
        onClose={() => setDownloadZipModalIsOpen(false)}
        caseId={caseId || ''}
        caseName={caseData?.name || 'Documents'}
        onStartZipCreation={handleCheckExisting}
        onDownloadZip={handleDownloadZip}
        onCheckExisting={handleCheckExisting}
        zipExists={zipExists}
        zipInProgress={zipInProgress}
        zipError={zipError}
        hasActiveFilters={!!buildZipFilters()}
        totalDocuments={responseDocs?.totalCount}
      />
    </div>
  );
};

export default DocumentEditor;

const documentListDownloadOptions = [
  { label: 'ID', value: 'ID' },
  { label: 'Filename', value: 'Filename' },
  { label: 'File Type', value: 'File Type' },
  { label: 'Upload Date', value: 'Upload Date' },
  { label: 'Author', value: 'Author' },
  { label: 'Date', value: 'Date' },
  { label: 'Document Type', value: 'Document Type' },
  { label: 'Summary', value: 'Summary' },
  { label: 'Number Of Pages', value: 'Number Of Pages' },
  { label: 'Number Of Facts', value: 'Number Of Facts' },
  { label: 'Contains Key Facts', value: 'Contains Key Facts' },
  { label: 'Contains Relevant Facts', value: 'Contains Relevant Facts' },
  { label: 'Issues', value: 'Issues' },
];
